import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, Sparkles } from "lucide-react";
import { nav, BOOK_URL } from "../mock";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Live Your Best Life">
    <div className="relative h-11 w-11 rounded-full border border-lybl-green/40 flex items-center justify-center">
      <span className="font-serif text-lybl-green text-lg leading-none">L</span>
      <span className="absolute inset-0 rounded-full border border-lybl-green/15 scale-[1.18]" />
    </div>
    <div className="leading-tight hidden sm:block">
      <p className="font-serif text-[13px] tracking-[0.18em] text-lybl-green uppercase">Live Your</p>
      <p className="font-serif text-[13px] tracking-[0.18em] text-lybl-green uppercase">Best Life</p>
    </div>
  </Link>
);

const Dropdown = ({ label, items }) => (
  <div className="relative group">
    <button className="flex items-center gap-1 text-[15px] text-lybl-ink/90 hover:text-lybl-green transition-colors py-2">
      {label}
      <ChevronDown className="h-4 w-4 mt-0.5 transition-transform group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
      <div className="w-64 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 p-2">
        {items.map((it) => (
          <Link key={it.label} to={it.to} className="block rounded-xl px-3 py-2.5 text-[14px] text-lybl-ink/80 hover:bg-lybl-cream hover:text-lybl-green transition-colors">
            {it.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-lybl-paper/90 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
      <nav className="mx-auto max-w-7xl px-5 lg:px-8 h-20 flex items-center justify-between">
        <Logo />
        <div className="hidden lg:flex items-center gap-7">
          <Dropdown label="Programs" items={nav.programs} />
          <Dropdown label="Supplements" items={nav.supplements} />
          <Link to="/evidence-library" className="text-[15px] text-lybl-ink/90 hover:text-lybl-green transition-colors">Evidence Library</Link>
          <Dropdown label="Explore" items={nav.explore} />
          <Dropdown label="Learn" items={nav.learn} />
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="dosha" className="inline-flex items-center gap-1.5 rounded-full bg-lybl-rust px-4 py-2.5 text-[14px] font-medium text-white hover:bg-lybl-rustDark transition-colors">
            <Sparkles className="h-4 w-4" /> Discover Your Dosha
          </a>
          <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="inline-flex items-center gap-1.5 rounded-full bg-lybl-green px-4 py-2.5 text-[14px] font-medium text-white hover:bg-lybl-greenDark transition-colors">
            Schedule Free Consult <span aria-hidden>→</span>
          </a>
        </div>
        <button className="lg:hidden p-2 text-lybl-green" onClick={() => setOpen(true)} aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>
      </nav>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-lybl-paper">
          <div className="flex items-center justify-between px-5 h-20">
            <Logo />
            <button className="p-2 text-lybl-green" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-6 w-6" /></button>
          </div>
          <div className="px-6 py-4 space-y-1 overflow-y-auto">
            <p className="text-xs uppercase tracking-wider text-lybl-muted mt-2 mb-1">Programs</p>
            {nav.programs.map((it) => (
              <Link key={it.label} to={it.to} onClick={() => setOpen(false)} className="block py-2 text-lybl-ink/85">{it.label}</Link>
            ))}
            <div className="pt-4 space-y-3">
              <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="dosha" className="block text-center rounded-full bg-lybl-rust px-4 py-3 text-white">Discover Your Dosha</a>
              <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="block text-center rounded-full bg-lybl-green px-4 py-3 text-white">Schedule Free Consult →</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
